import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useGetAllCatalogMeds } from '../../MedicationOrderSets/hooks/useGetAllCatalogMeds';
import { useGetAllCatalogMedsOptions } from '../../MedForm/hooks/useGetAllCatalogMedsOptions';

export type CatalogMedsFilter = {
  medName?: string;
  brand?: string;
  indication?: string;
  therapeuticArea?: string;
  therapeuticClass?: string;
  isDoseSchedule?: boolean;
  isPrn?: boolean;
  isTaper?: boolean;
};

type CatalogOptions = {
  indicationOptions: string[];
  therapeuticAreaOptions: string[];
  therapeuticClassOptions: string[];
};

type ProviderState = {
  meds: any[];
  isLoading: boolean;
  errorMessage: string;
  filters: CatalogMedsFilter;
  page: number;
  pageSize: number;
  totalPages: number;
  setFilters: (next: CatalogMedsFilter) => void;
  setPage: (next: number) => void;
  setPageSize: (next: number) => void;
  applyFiltersAndFetch: (
    next: CatalogMedsFilter,
    opts?: { force?: boolean; page?: number; pageSize?: number },
  ) => Promise<void>;
  fetchIfNeeded: (opts?: { force?: boolean }) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  lastUpdatedAt?: number;
  // Shared options for forms/filters
  catalogOptions: CatalogOptions;
  refreshCatalogOptions: () => Promise<void>;
};

const MedicationMedCatalogueContext = createContext<ProviderState | undefined>(
  undefined,
);

function stableKey(obj: unknown): string {
  try {
    return JSON.stringify(obj, Object.keys(obj as any).sort());
  } catch {
    return JSON.stringify(obj);
  }
}

export const MedicationMedCatalogueProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { postGetAllCatalogMeds, isLoading, errorMessage, medicineData } =
    useGetAllCatalogMeds();
  const { optionsData, postGetAllCatalogMedsOptions } =
    useGetAllCatalogMedsOptions();

  const [filters, setFiltersState] = useState<CatalogMedsFilter>({});
  const [page, setPageState] = useState<number>(1);
  const [pageSize, setPageSizeState] = useState<number>(30);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [meds, setMeds] = useState<any[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | undefined>(
    undefined,
  );
  const [catalogOptions, setCatalogOptions] = useState<CatalogOptions>({
    indicationOptions: [],
    therapeuticAreaOptions: [],
    therapeuticClassOptions: [],
  });

  const lastPayloadKeyRef = useRef<string>('');
  const lastTotalPagesKeyRef = useRef<string>('');

  const setFilters = useCallback((next: CatalogMedsFilter) => {
    setFiltersState(next);
  }, []);

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setTotalPages(0);
  }, []);

  const buildPayload = useCallback(
    () => ({
      ...filters,
      page,
      pageSize,
    }),
    [filters, page, pageSize],
  );

  const fetchIfNeeded = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = !!opts?.force;
      const payload = buildPayload();
      const totalPagesKey = stableKey({ ...filters, pageSize });
      const shouldIncludeTotalPages =
        totalPagesKey !== lastTotalPagesKeyRef.current || totalPages === 0;
      const requestPayload = {
        ...payload,
        ...(shouldIncludeTotalPages ? { includeTotalPages: true } : {}),
      };
      const nextKey = stableKey(requestPayload);

      if (!force && nextKey === lastPayloadKeyRef.current) {
        // Payload unchanged; skip network
        return;
      }

      const result = await postGetAllCatalogMeds({
        filterPayload: requestPayload,
      });
      if (Array.isArray(result)) {
        setMeds(result);
        if (shouldIncludeTotalPages) {
          const nextTotalPages = (result as any).__totalPages;
          if (typeof nextTotalPages === 'number') {
            setTotalPages(nextTotalPages);
            lastTotalPagesKeyRef.current = totalPagesKey;
          }
        }
      } else {
        // Fallback to hook's internal state when API returns undefined
        setMeds(Array.isArray(medicineData) ? medicineData : []);
      }
      lastPayloadKeyRef.current = nextKey;
      setLastUpdatedAt(Date.now());
    },
    [
      buildPayload,
      filters,
      pageSize,
      totalPages,
      postGetAllCatalogMeds,
      medicineData,
    ],
  );

  const applyFiltersAndFetch = useCallback(
    async (
      next: CatalogMedsFilter,
      opts?: { force?: boolean; page?: number; pageSize?: number },
    ) => {
      // Update filters immediately
      setFiltersState(next);
      // Build payload using the provided overrides to avoid race conditions
      const effectivePage = typeof opts?.page === 'number' ? opts.page : page;
      const effectivePageSize =
        typeof opts?.pageSize === 'number' ? opts.pageSize : pageSize;
      const payload = {
        ...next,
        page: effectivePage,
        pageSize: effectivePageSize,
      };
      const totalPagesKey = stableKey({ ...next, pageSize: effectivePageSize });
      const shouldIncludeTotalPages =
        totalPagesKey !== lastTotalPagesKeyRef.current || totalPages === 0;
      const requestPayload = {
        ...payload,
        ...(shouldIncludeTotalPages ? { includeTotalPages: true } : {}),
      };
      const nextKey = stableKey(requestPayload);

      const result = await postGetAllCatalogMeds({
        filterPayload: requestPayload,
      });
      if (Array.isArray(result)) {
        setMeds(result);
        if (shouldIncludeTotalPages) {
          const nextTotalPages = (result as any).__totalPages;
          if (typeof nextTotalPages === 'number') {
            setTotalPages(nextTotalPages);
            lastTotalPagesKeyRef.current = totalPagesKey;
          }
        }
      } else {
        setMeds(Array.isArray(medicineData) ? medicineData : []);
      }
      lastPayloadKeyRef.current = nextKey;
      setLastUpdatedAt(Date.now());
    },
    [postGetAllCatalogMeds, page, pageSize, totalPages, medicineData],
  );

  const refresh = useCallback(async () => {
    await fetchIfNeeded({ force: true });
  }, [fetchIfNeeded]);

  const reset = useCallback(() => {
    setFiltersState({});
    setPageState(1);
    setPageSizeState(30);
    setTotalPages(0);
    lastPayloadKeyRef.current = '';
    lastTotalPagesKeyRef.current = '';
    setMeds([]);
    setLastUpdatedAt(undefined);
  }, []);

  const refreshCatalogOptions = useCallback(async () => {
    await postGetAllCatalogMedsOptions();
  }, [postGetAllCatalogMedsOptions]);

  React.useEffect(() => {
    refreshCatalogOptions();
  }, []);

  React.useEffect(() => {
    if (optionsData) {
      setCatalogOptions(optionsData);
    }
  }, [optionsData]);

  const value = useMemo<ProviderState>(
    () => ({
      meds,
      isLoading,
      errorMessage,
      filters,
      page,
      pageSize,
      totalPages,
      setFilters,
      setPage,
      setPageSize,
      applyFiltersAndFetch,
      fetchIfNeeded,
      refresh,
      reset,
      lastUpdatedAt,
      catalogOptions,
      refreshCatalogOptions,
    }),
    [
      meds,
      isLoading,
      errorMessage,
      filters,
      page,
      pageSize,
      totalPages,
      setFilters,
      setPage,
      setPageSize,
      applyFiltersAndFetch,
      fetchIfNeeded,
      refresh,
      reset,
      lastUpdatedAt,
      catalogOptions,
      refreshCatalogOptions,
    ],
  );

  return (
    <MedicationMedCatalogueContext.Provider value={value}>
      {children}
    </MedicationMedCatalogueContext.Provider>
  );
};

export function useMedicationMedCatalogue() {
  const ctx = useContext(MedicationMedCatalogueContext);
  if (!ctx)
    throw new Error(
      'useMedicationMedCatalogue must be used within MedicationMedCatalogueProvider',
    );
  return ctx;
}
